import { useState, useEffect } from 'react';
import { Modal, Button, Group, Select, Stack, Text, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

interface Person {
  id: string;
  full_name: string;
  email?: string | null;
}

interface BulkUpdateModalProps {
  opened: boolean;
  onClose: () => void;
  onUpdate: (updates: BulkUpdateData) => void;
  selectedCount: number;
  isLoading?: boolean;
  people?: Person[];
}

export interface BulkUpdateData {
  status?: string;
  location_id?: string | null;
  category_id?: string | null;
  department_id?: string | null;
  person_id?: string | null;
}

export function BulkUpdateModal({
  opened,
  onClose,
  onUpdate,
  selectedCount,
  isLoading,
  people = [],
}: BulkUpdateModalProps) {
  const [status, setStatus] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(null);

  // If status changes to Assigned, we need a person. If it changes away from Assigned, clear person.
  useEffect(() => {
    if (status && status !== 'Assigned') {
      setPersonId(null);
    }
  }, [status]);

  const handleSubmit = () => {
    const updates: BulkUpdateData = {};

    if (status) updates.status = status;
    if (locationId) updates.location_id = locationId;
    if (categoryId) updates.category_id = categoryId;
    if (departmentId) updates.department_id = departmentId;
    if (personId) updates.person_id = personId;

    // If assigning to a person but no status set, auto-set to Assigned
    if (personId && !status) {
      updates.status = 'Assigned';
    }

    onUpdate(updates);
    handleClose();
  };

  const handleClose = () => {
    setStatus(null);
    setLocationId(null);
    setCategoryId(null);
    setDepartmentId(null);
    setPersonId(null);
    onClose();
  };

  const hasChanges = status || locationId || categoryId || departmentId || personId;

  // Validation: if status is Assigned, person must be selected
  const needsPersonForAssigned = status === 'Assigned' && !personId;
  const canSubmit = hasChanges && !needsPersonForAssigned;

  const peopleOptions = people.map((person) => ({
    value: person.id,
    label: `${person.full_name}${person.email ? ` (${person.email})` : ''}`,
  }));

  return (
    <Modal opened={opened} onClose={handleClose} title="Bulk Update Assets" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Update {selectedCount} selected asset{selectedCount !== 1 ? 's' : ''}. Only fields you
          change will be updated.
        </Text>

        <Select
          label="Status"
          placeholder="No change"
          data={[
            { value: 'Available', label: 'Available' },
            { value: 'Assigned', label: 'Assigned' },
            { value: 'Maintenance', label: 'Maintenance' },
            { value: 'Retired', label: 'Retired' },
            { value: 'Lost', label: 'Lost' },
          ]}
          value={status}
          onChange={setStatus}
          clearable
        />

        <Select
          label="Assign To"
          placeholder={status === 'Assigned' ? 'Select a person (required)' : 'No change'}
          data={peopleOptions}
          value={personId}
          onChange={setPersonId}
          searchable
          clearable
          disabled={status !== null && status !== 'Assigned'}
          description={
            status === 'Assigned'
              ? 'Required when status is Assigned'
              : personId
                ? 'Status will be set to Assigned'
                : undefined
          }
        />

        {needsPersonForAssigned && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
            Please select a person to assign these assets to.
          </Alert>
        )}

        <Select
          label="Location"
          placeholder="No change"
          data={[]}
          value={locationId}
          onChange={setLocationId}
          clearable
          disabled
        />

        <Select
          label="Category"
          placeholder="No change"
          data={[]}
          value={categoryId}
          onChange={setCategoryId}
          clearable
          disabled
        />

        <Select
          label="Department"
          placeholder="No change"
          data={[]}
          value={departmentId}
          onChange={setDepartmentId}
          clearable
          disabled
        />

        <Text size="xs" c="dimmed">
          Note: Location, Category, and Department dropdowns will be enabled once those entities
          are loaded.
        </Text>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isLoading} loading={isLoading}>
            Update {selectedCount} Asset{selectedCount !== 1 ? 's' : ''}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
